import { useState, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Terminal as TerminalIcon, Plus, X, Server, Box } from 'lucide-react';
import { terminalApi, TerminalSession, TerminalSessionCreate } from '../../lib/api/terminal';
import { serversApi } from '../../lib/api/servers';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { XTerminal } from '../../components/terminal/XTerminal';

export function TerminalPage() {
  const [activeSession, setActiveSession] = useState<TerminalSession | null>(null);
  const [showNewSession, setShowNewSession] = useState(false);
  const [selectedServer, setSelectedServer] = useState('');
  const [terminalType, setTerminalType] = useState<'host' | 'container'>('host');
  const [containerName, setContainerName] = useState('');
  const [wsConnected, setWsConnected] = useState(false);
  const queryClient = useQueryClient();

  const { data: sessions } = useQuery({
    queryKey: ['terminal-sessions'],
    queryFn: () => terminalApi.listSessions(),
  });

  const { data: servers } = useQuery({
    queryKey: ['servers'],
    queryFn: () => serversApi.list({ limit: 100 }),
  });

  // Fetch containers when server is selected and container type chosen
  const { data: containers, isLoading: containersLoading } = useQuery({
    queryKey: ['containers', selectedServer],
    queryFn: () => terminalApi.listContainers(selectedServer),
    enabled: !!selectedServer && terminalType === 'container',
  });

  // Reset container selection when server or type changes
  useEffect(() => {
    setContainerName('');
  }, [selectedServer, terminalType]);

  const createSession = useMutation({
    mutationFn: (data: TerminalSessionCreate) => terminalApi.createSession(data),
    onSuccess: (session) => {
      queryClient.invalidateQueries({ queryKey: ['terminal-sessions'] });
      setActiveSession(session);
      setShowNewSession(false);
      // Reset form
      setSelectedServer('');
      setTerminalType('host');
      setContainerName('');
    },
  });

  const closeSession = useMutation({
    mutationFn: (id: string) => terminalApi.closeSession(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['terminal-sessions'] });
      setActiveSession(null);
      setWsConnected(false);
    },
  });

  const handleConnect = useCallback(() => {
    setWsConnected(true);
  }, []);

  const handleDisconnect = useCallback(() => {
    setWsConnected(false);
  }, []);

  const handleCreateSession = () => {
    if (!selectedServer) return;
    if (terminalType === 'container' && !containerName) return;

    createSession.mutate({
      server_id: selectedServer,
      terminal_type: terminalType,
      container_name: terminalType === 'container' ? containerName : undefined,
      cols: 120,
      rows: 30,
    });
  };

  const handleSelectSession = (session: TerminalSession) => {
    // If switching sessions, reset connection state
    if (activeSession?.id !== session.id) {
      setWsConnected(false);
      setActiveSession(session);
    }
  };

  const canConnect = selectedServer && (terminalType === 'host' || (terminalType === 'container' && containerName));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Terminal</h1>
          <p className="text-gray-600">Connect to servers via web terminal</p>
        </div>
        <Button onClick={() => setShowNewSession(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New Session
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sessions List */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Active Sessions</CardTitle>
            </CardHeader>
            <CardContent className="p-2">
              {sessions?.length === 0 ? (
                <p className="text-sm text-gray-500 p-2">No active sessions</p>
              ) : (
                <div className="space-y-1">
                  {sessions?.map((session) => (
                    <button
                      key={session.id}
                      onClick={() => handleSelectSession(session)}
                      className={`w-full p-2 text-left rounded-md text-sm transition-colors ${
                        activeSession?.id === session.id
                          ? 'bg-primary-100 text-primary-700'
                          : 'hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {session.terminal_type === 'container' ? (
                          <Box className="h-4 w-4" />
                        ) : (
                          <Server className="h-4 w-4" />
                        )}
                        <span className="truncate">
                          {session.terminal_type === 'container'
                            ? session.container_name
                            : session.server_id.slice(0, 8) + '...'}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {session.terminal_type}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Terminal */}
        <div className="lg:col-span-3">
          <Card className="flex flex-col" style={{ height: 'calc(100vh - 200px)', minHeight: '500px' }}>
            <CardHeader className="flex-row items-center justify-between border-b flex-shrink-0 py-3">
              <CardTitle className="flex items-center gap-2">
                <TerminalIcon className="h-5 w-5" />
                {activeSession ? 'Terminal' : 'No Active Session'}
                {wsConnected && (
                  <span className="h-2 w-2 rounded-full bg-green-500" />
                )}
              </CardTitle>
              {activeSession && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => closeSession.mutate(activeSession.id)}
                  disabled={closeSession.isPending}
                >
                  {closeSession.isPending ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
                  ) : (
                    <X className="h-4 w-4" />
                  )}
                </Button>
              )}
            </CardHeader>
            <div className="flex-1 min-h-0">
              {activeSession ? (
                <XTerminal
                  key={activeSession.id}
                  sessionId={activeSession.id}
                  onConnect={handleConnect}
                  onDisconnect={handleDisconnect}
                />
              ) : (
                <div className="h-full flex items-center justify-center text-gray-500 bg-gray-50">
                  <div className="text-center">
                    <TerminalIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>Select a session or create a new one</p>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>

      {/* New Session Modal */}
      {showNewSession && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium mb-4">New Terminal Session</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Server</label>
                <select
                  value={selectedServer}
                  onChange={(e) => setSelectedServer(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Select a server</option>
                  {servers?.items.map((server) => (
                    <option key={server.id} value={server.id}>
                      {server.name} ({server.host})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Terminal Type</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      value="host"
                      checked={terminalType === 'host'}
                      onChange={() => setTerminalType('host')}
                      className="text-primary-600"
                    />
                    <Server className="h-4 w-4" />
                    Host
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      value="container"
                      checked={terminalType === 'container'}
                      onChange={() => setTerminalType('container')}
                      className="text-primary-600"
                    />
                    <Box className="h-4 w-4" />
                    Container
                  </label>
                </div>
              </div>

              {terminalType === 'container' && selectedServer && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Container</label>
                  {containersLoading ? (
                    <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
                      Loading containers...
                    </div>
                  ) : containers && containers.length > 0 ? (
                    <select
                      value={containerName}
                      onChange={(e) => setContainerName(e.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">Select a container</option>
                      {containers.map((container) => (
                        <option key={container.id} value={container.name}>
                          {container.name} ({container.image})
                        </option>
                      ))}
                    </select>
                  ) : (
                    <p className="text-sm text-gray-500 py-2">No running containers found</p>
                  )}
                </div>
              )}

              {terminalType === 'container' && !selectedServer && (
                <p className="text-sm text-gray-500">Select a server first to see available containers</p>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowNewSession(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleCreateSession}
                disabled={!canConnect || createSession.isPending}
              >
                {createSession.isPending ? 'Connecting...' : 'Connect'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
